/**
 * @description Returns a random number between min (inclusive) and max (exclusive)
 * @param min {Number}
 * @param max {Number}
 * @return {Number}
 */
export const randomArbitrary = ( min: number, max: number ): number => {
	return Math.random() * ( max - min ) + min;
};

/**
 * @description Returns a random integer between min (inclusive) and max (inclusive).
 * The value is no lower than min (or the next integer greater than min
 * if min isn't an integer) and no greater than max (or the next integer
 * lower than max if max isn't an integer).
 * Using Math.round() will give you a non-uniform distribution!
 * @param min {Number}
 * @param max {Number}
 * @return {Number}
 */
export const random = ( min: number, max: number ): number => {
	min = Math.ceil( min );
	max = Math.floor( max );
	return Math.floor( Math.random() * ( max - min + 1 ) ) + min;
};

/**
 * @description Clamp number between min and max values
 * @param num {Number}
 * @param min {Number}
 * @param max {Number}
 * @return {Number}
 * @example
 * ```typescript
 * var myNum = 110;
 * var min = 0;
 * var max = 100;
 * var myNum = numClamp( myNum, min, max ); // returns 100 (within 0 - 100 range)
 * ```
 */
export const clamp = ( num: number, min: number, max: number ): number => {
	return Math.min( Math.max( num, min ), max );
};

/**
 * @description Round a Number to decimal points
 * @see {@link http://www.jacklmoore.com/notes/rounding-in-javascript/}
 * @param value {Number}
 * @param decimals {Number}
 * @return {Number}
 */
export const roundToDecimal = ( value: number = 0, decimals: number = 0 ): number => {
	return parseFloat( ( value ).toFixed( decimals ) );
	// return Number( Math.round( Number( value + 'e' + decimals ) ) + 'e-' + decimals );
};

/**
 * @description Count number of decimal places in number
 * @see {@link https://stackoverflow.com/a/55700334}
 * @param num {Number}
 * @return {Number}
 */
export const decimals = ( num: number ): number => {
	const text: string = num.toString();
	if ( text.indexOf( 'e-' ) > -1 ) {
		const [ base, trail ] = text.split( 'e-' );
		const elen = parseInt( trail, 10 );
		const idx = base.indexOf( '.' );
		return idx === -1 ? ( elen ) : ( base.length - idx - 1 ) + elen;
	}
	const index = text.indexOf( '.' );
	return index === -1 ? 0 : ( text.length - index - 1 );
};

/**
 * @description Get percent for value between min - max range
 * @param input {Number}
 * @param min {Number}
 * @param max {Number}
 * @return {Number|undefined}
 */
export const percentWithinRange = ( input: number, min: number, max: number ): number | undefined => {
	if ( isNaN( input ) || isNaN( min ) || isNaN( max ) ) return undefined;
	return ( ( input - min ) * 100 ) / ( max - min );
};

/**
 * @description Get value for percent between min - max range
 * @param percent {Number}
 * @param min {Number}
 * @param max {Number}
 * @return {Number|undefined}
 */
export const valueWithinRange = ( percent: number, min: number, max: number ): number | undefined => {
	if ( isNaN( percent ) || isNaN( min ) || isNaN( max ) ) return undefined;
	return ( ( percent * ( max - min ) / 100 ) + min );
};

/**
 * @description Check if 2 float numbers are within a given range
 * @param numA {Number}
 * @param numB {Number}
 * @param range {Number}
 * @return {boolean | undefined}
 */
export const floatsWithinRange = ( numA: number, numB: number, range: number ): boolean | undefined => {
	if ( isNaN( numA ) || isNaN( numB ) || isNaN( range ) ) return undefined;
	return Math.abs( numA - numB ) <= range;
};